import React, { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faPencil } from '@fortawesome/free-solid-svg-icons/faPencil';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import classNames from 'classnames';

import AlertStatic from '@buyside/components/presentation/AlertStatic/alertStatic';
import AllocatorCardForm from '@buyside/components/presentation/AllocatorProfileShowPage/AllocatorProfileForm/CardForm/cardForm';
import AllocatorProfileFormModalContainer from '@buyside/components/container/AllocatorProfileShow/AllocatorProfileFormModalContainer';
import AllocatorProfileCardRecommendationContainer from '@buyside/components/container/AllocatorProfileIndex/AllocatorProfileCardRecommendationContainer';
import BriefcaseIcon from '@buyside/svgs/briefcase.svg';
import Button, { BUTTON_TYPES, BUTTON_SIZES } from '@buyside/components/presentation/Button/button';
import EditableImage from '@buyside/components/presentation/EditableImage/editableImage';
import FollowButton, {
  FollowableType
} from '@buyside/components/presentation/FollowButton/followButton';
import FundCardForm from '@buyside/components/presentation/FundProfileShowPage/FundProfileForm/CardForm/cardForm';
import FundProfileFormModalContainer from '@buyside/components/container/FundProfileShow/FundProfileFormModalContainer';
import FundProfileCardRecommendationContainer from '@buyside/components/container/FundProfileIndex/FundProfileCardRecommendationContainer';
import Link, { LINK_TYPES } from '@buyside/components/presentation/Link/link';
import MessageModal from '@buyside/components/presentation/MessageModal/messageModal';
import Modal from '@buyside/components/presentation/Modal/modal';
import ParsedHtml from '@buyside/components/presentation/ParsedHtml/parsedHtml';
import PersonWithBriefcase from '@buyside/svgs/person_with_briefcase.svg';
import PieChartIcon from '@buyside/svgs/pie_chart.svg';
import TooltipWhite from '@buyside/components/presentation/Tooltips/TooltipWhite/tooltipWhite';
import UserCardForm from '@buyside/components/presentation/UserProfileShowPage/UserProfileForm/CardForm/cardForm';
import UserProfileFormModalContainer from '@buyside/components/container/UserProfileShow/UserProfileFormModalContainer';

import { cardSchema as allocatorCardSchema } from '@buyside/util/allocatorProfileShowPageValidationSchemata';
import { cardSchema as fundCardSchema } from '@buyside/util/fundProfileShowPageValidationSchemata';
import { cardSchema as userCardSchema } from '@buyside/util/userProfileShowPageValidationSchemata';

import { get } from '@buyside/util/lodashAltJs';
import { defaultUserImageUrl } from '@buyside/util/urlHelper';
import { truncateText } from '@buyside/util/textHelpers';
import { useIsMobile } from '@buyside/util/hooks/useIsMobile';

import {
  AllocatorProfileRecommendationFragment,
  AllocatorProfileShowPageQuery,
  FundProfileRecommendationFragment,
  FundProfileShowPageQuery,
  Maybe,
  UserProfileShowPageQuery
} from '@interfaces/graphql-typings';

import styles from './profileHeaderStyles.scss';

export enum PROFILE_TYPES {
  ALLOCATOR = 'allocator',
  FUND = 'fund',
  USER = 'user'
}

export interface ProfileHeaderProps {
  datapoints?: { title: string; value: string }[];
  displayName: string;
  isCurrentUser?: boolean;
  mainDatapoints?: { icon: IconDefinition; title: string; href: string; tooltip: string }[];
  messageModalProps?: {
    createMessage: () => void;
    isCurrentEmployer: boolean;
    receiverEmployerName: string;
    receiverId: string;
    receiverName: string;
  };
  profile:
    | UserProfileShowPageQuery['userProfile']
    | FundProfileShowPageQuery['fundProfile']
    | AllocatorProfileShowPageQuery['allocatorProfile'];
  profileImageUrl: string;
  profileType: PROFILE_TYPES.ALLOCATOR | PROFILE_TYPES.FUND | PROFILE_TYPES.USER;
  recommendation?: Maybe<
    FundProfileRecommendationFragment | AllocatorProfileRecommendationFragment
  >;
  roleIsAdmin: boolean;
  saveBackgroundImage: () => void;
  shrink: boolean;
  summaryHtml: Maybe<string>;
  updateCard: () => void;
}

const ProfileHeader = forwardRef<{ openEditModal: () => void }, ProfileHeaderProps>(
  (props, ref) => {
    const isMobile = useIsMobile();

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

    const openEditModal = (): void => setEditModalOpen(true);
    const closeEditModal = (): void => setEditModalOpen(false);

    useImperativeHandle(ref, () => ({
      openEditModal
    }));

    const {
      datapoints,
      displayName,
      isCurrentUser,
      mainDatapoints,
      messageModalProps,
      profile,
      profileImageUrl,
      profileType,
      recommendation,
      roleIsAdmin,
      saveBackgroundImage,
      shrink,
      summaryHtml,
      updateCard
    } = props;

    const followableId =
      profileType === PROFILE_TYPES.USER ? get(profile, 'user.id') : get(profile, 'id');
    const followableType =
      profileType === PROFILE_TYPES.USER ? FollowableType.USER : FollowableType.FUND;

    const renderOpenDescriptionModalButton = useCallback(
      () => (
        <Button
          className={styles.readMoreButton}
          onClick={() => setIsDescriptionModalOpen(true)}
          text={isMobile ? `About ${displayName}` : 'Read More'}
          type={BUTTON_TYPES.LINK}
        />
      ),
      [isMobile, displayName]
    );

    return (
      <div
        id="profile-header"
        className={classNames(styles.profileHeader, { [styles.shrink]: shrink })}
      >
        {recommendation && (
          <>
            {profileType === PROFILE_TYPES.FUND && (
              <FundProfileCardRecommendationContainer node={recommendation} ProfileType="Buyside" />
            )}
            {profileType === PROFILE_TYPES.ALLOCATOR && (
              <AllocatorProfileCardRecommendationContainer
                node={recommendation}
                ProfileType="Allocator"
              />
            )}
          </>
        )}
        <EditableImage
          croppedImage={profile?.backgroundImageCroppedUrl}
          defaultImage={profile?.defaultBackgroundImage}
          image={profile?.backgroundImageUrl}
          isBackground
          isEditable={roleIsAdmin}
          saveImage={saveBackgroundImage}
          title="background-image"
        />
        <div className={styles.profileHeaderTop}>
          {profileType === PROFILE_TYPES.ALLOCATOR ? (
            <PieChartIcon
              data-testid="pie-chart-icon"
              className={classNames(styles.profileImage, styles.profileImageIcon)}
            />
          ) : (
            <img className={styles.profileImage} src={profileImageUrl} />
          )}
          <div className={styles.profileHeaderButtons}>
            {!isCurrentUser && followableId && (
              <>
                {[PROFILE_TYPES.USER, PROFILE_TYPES.FUND].includes(profileType) && (
                  <FollowButton
                    id={followableId}
                    size={isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL}
                    type={followableType}
                  />
                )}
                {messageModalProps && (
                  <MessageModal
                    {...messageModalProps}
                    buttonOptions={{
                      faIcon: faEnvelope,
                      className: styles.messageButton,
                      size: isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL,
                      text: isMobile ? '' : 'Message',
                      type: BUTTON_TYPES.SECONDARY
                    }}
                  />
                )}
              </>
            )}
            {roleIsAdmin && (
              <Button
                className={styles.editButton}
                faIcon={faPencil}
                onClick={openEditModal}
                size={isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL}
                text={isMobile ? '' : 'Edit'}
              />
            )}
          </div>
        </div>
        <h3>{displayName}</h3>
        {mainDatapoints && (
          <div className={styles.mainDatapoints}>
            {mainDatapoints.map(datapoint => {
              if (!datapoint) return null;

              const datapointLink = (
                <Link
                  className={styles.mainDatapointLink}
                  key={datapoint.title}
                  to={datapoint.href}
                  type={LINK_TYPES.PLAIN}
                >
                  {datapoint.icon && <FontAwesomeIcon icon={datapoint.icon} />}
                  {datapoint.title}
                </Link>
              );

              if (datapoint.tooltip)
                return (
                  <TooltipWhite
                    key={datapoint.title}
                    placement="bottom"
                    overlay={datapoint.tooltip}
                  >
                    {datapointLink}
                  </TooltipWhite>
                );

              return datapointLink;
            })}
          </div>
        )}
        {datapoints && (
          <div className={styles.datapoints}>
            {datapoints.map(datapoint =>
              datapoint ? (
                <span key={datapoint.title} className={styles.datapointLink}>
                  <strong>{datapoint.title}</strong>
                  {datapoint.value}
                </span>
              ) : null
            )}
          </div>
        )}
        {summaryHtml && (
          <div className={styles.profileDescription}>
            <ParsedHtml html={truncateText(summaryHtml, isMobile ? 150 : 300).replace(/\n/g, ' ')}>
              {({ content }): React.ReactElement => <>{content}</>}
            </ParsedHtml>
            {summaryHtml.length > (isMobile ? 150 : 300) && (
              <Modal
                isOpen={isDescriptionModalOpen}
                onClose={() => setIsDescriptionModalOpen(false)}
                renderOpenModalButton={renderOpenDescriptionModalButton}
                title={`About ${displayName}`}
              >
                <ParsedHtml html={summaryHtml.replace(/\n/g, '<br />')}>
                  {({ content }) => <>{content}</>}
                </ParsedHtml>
              </Modal>
            )}
          </div>
        )}
        {roleIsAdmin && (
          <>
            <div className={styles.profileAlerts}>
              {profileType !== PROFILE_TYPES.ALLOCATOR &&
                profileImageUrl === defaultUserImageUrl() && (
                  <AlertStatic>
                    <p>
                      Click the{' '}
                      <strong>
                        <FontAwesomeIcon icon={faPencil} /> Edit
                      </strong>{' '}
                      button above to add a profile picture to your profile.
                    </p>
                  </AlertStatic>
                )}
              {!summaryHtml && (
                <AlertStatic>
                  <p>
                    Click the{' '}
                    <strong>
                      <FontAwesomeIcon icon={faPencil} /> Edit
                    </strong>{' '}
                    button above to add a bio to your profile.
                  </p>
                </AlertStatic>
              )}
            </div>
            {profileType === PROFILE_TYPES.USER && (
              <UserProfileFormModalContainer
                closeModal={closeEditModal}
                form={UserCardForm}
                icon={PersonWithBriefcase}
                isOpen={editModalOpen}
                title="General Information"
                update={updateCard}
                userProfile={profile}
                validationSchema={userCardSchema}
              />
            )}
            {profileType === PROFILE_TYPES.FUND && (
              <FundProfileFormModalContainer
                closeModal={closeEditModal}
                form={FundCardForm}
                fundProfile={profile}
                icon={BriefcaseIcon}
                isOpen={editModalOpen}
                title="General Information"
                update={updateCard}
                validationSchema={fundCardSchema}
              />
            )}
            {profileType === PROFILE_TYPES.ALLOCATOR && (
              <AllocatorProfileFormModalContainer
                allocatorProfile={profile}
                closeModal={closeEditModal}
                form={AllocatorCardForm}
                icon={PieChartIcon}
                isOpen={editModalOpen}
                title="General Information"
                update={updateCard}
                validationSchema={allocatorCardSchema}
                modalId="allocator-profile-form"
              />
            )}
          </>
        )}
      </div>
    );
  }
);

export default ProfileHeader;
